import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faGear, faCircleNotch, faClock, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-modal';
import useIsMobile from 'common/hooks/useIsMobile';
import DeviceChecklistService from 'api/deviceChecklist/DeviceChecklistService';

interface FotaApplyModalProps {
    open: boolean;
    imei: string;
    fileId: number;
    deviceId: string;
    simCardCode: string;
    km: number;
    deviceModelName: string;
    onClose: (success: boolean) => void;
}

type StepStatus = 'pending' | 'running' | 'done' | 'error' | 'info';

interface StepState {
    key: string;
    status: StepStatus;
    error?: string;
}

const POLL_INTERVAL = 5000;
const POLL_TIMEOUT = 60;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const FotaApplyModal: React.FC<FotaApplyModalProps> = ({ open, imei, fileId, deviceId, simCardCode, km, deviceModelName, onClose }) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [steps, setSteps] = useState<StepState[]>([]);
    const [countdown, setCountdown] = useState(POLL_TIMEOUT);
    const [finished, setFinished] = useState(false);
    const [success, setSuccess] = useState(false);
    const [globalError, setGlobalError] = useState<string | null>(null);
    const abortRef = useRef(false);
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const taskIdRef = useRef<number | null>(null);
    const isDeviceOnlineRef = useRef(false);
    const runRef = useRef<() => Promise<void>>();

    const stopCountdown = useCallback(() => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
    }, []);

    const updateStepByIndex = useCallback((idx: number, status: StepStatus, error?: string) => {
        setSteps(prev => prev.map((s, i) => i === idx
            ? { ...s, status, error }
            : s
        ));
    }, []);

    /** Poll task status for up to POLL_TIMEOUT seconds. Returns 'completed' | 'failed' | 'timeout'. */
    const pollTaskStatus = useCallback(async (taskId: number, stepIdx: number): Promise<'completed' | 'failed' | 'timeout'> => {
        let remaining = POLL_TIMEOUT;
        setCountdown(remaining);
        countdownRef.current = setInterval(() => {
            remaining -= 1;
            setCountdown(Math.max(0, remaining));
            if (remaining <= 0 && countdownRef.current) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
            }
        }, 1000);

        const startTime = Date.now();
        while (Date.now() - startTime < POLL_TIMEOUT * 1000) {
            if (abortRef.current) { stopCountdown(); return 'failed'; }
            await delay(POLL_INTERVAL);
            if (abortRef.current) { stopCountdown(); return 'failed'; }
            try {
                const status = await DeviceChecklistService.getFotaTaskStatus(taskId);
                if (status.sessionExpired) {
                    stopCountdown();
                    updateStepByIndex(stepIdx, 'error', 'Session expired.');
                    setGlobalError('Session expired.');
                    setFinished(true);
                    return 'failed';
                }
                if (status.statusId === 2) {
                    stopCountdown();
                    return 'completed';
                }
                if (status.statusId >= 3) {
                    stopCountdown();
                    updateStepByIndex(stepIdx, 'error', status.statusName || t('deviceChecklist.fota_apply_failed'));
                    setGlobalError(t('deviceChecklist.fota_apply_failed'));
                    setFinished(true);
                    return 'failed';
                }
            } catch {
                // Network error — keep polling
            }
        }
        stopCountdown();
        return 'timeout';
    }, [stopCountdown, updateStepByIndex, t]);

    /** Poll TruePhone delivery report for SMS with given content. Returns true if found. */
    const pollSmsStatus = useCallback(async (iccid: string, content: string): Promise<boolean> => {
        let remaining = POLL_TIMEOUT;
        setCountdown(remaining);
        countdownRef.current = setInterval(() => {
            remaining -= 1;
            setCountdown(Math.max(0, remaining));
            if (remaining <= 0 && countdownRef.current) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
            }
        }, 1000);

        const startTime = Date.now();
        while (Date.now() - startTime < POLL_TIMEOUT * 1000) {
            if (abortRef.current) { stopCountdown(); return false; }
            await delay(POLL_INTERVAL);
            if (abortRef.current) { stopCountdown(); return false; }
            try {
                const result = await DeviceChecklistService.getSmsStatus(iccid, content);
                if (result.found) {
                    stopCountdown();
                    return true;
                }
            } catch {
                // Network error — keep polling
            }
        }
        stopCountdown();
        return false;
    }, [stopCountdown]);

    type StepAction = (stepIdx: number) => Promise<boolean>;

    const buildStepActions = useCallback((isOnline: boolean): StepAction[] => {
        const actions: StepAction[] = [];

        // Placeholder for the check-status step (already completed before actions run)
        actions.push(async () => true);

        if (!isOnline) {
            // Send setparam
            actions.push(async (idx) => {
                updateStepByIndex(idx, 'running');
                const result = await DeviceChecklistService.sendSetParam(deviceId);
                if (abortRef.current) return false;
                if (!result.ok) {
                    updateStepByIndex(idx, 'error', result.error || 'Failed.');
                    setGlobalError(result.error || t('deviceChecklist.fota_apply_failed'));
                    return false;
                }
                updateStepByIndex(idx, 'done');
                return true;
            });

            // Wait for setparam acknowledgment
            actions.push(async (idx) => {
                updateStepByIndex(idx, 'running');
                const ack = await pollSmsStatus(simCardCode, 'New value 2001:iot.truphone.com;2004:gps.sparkleit.pt;2005:12050;2006:1;');
                if (abortRef.current) return false;
                if (!ack) {
                    updateStepByIndex(idx, 'error', t('deviceChecklist.fota_apply_setparam_timeout'));
                    setGlobalError(t('deviceChecklist.fota_apply_setparam_timeout'));
                    return false;
                }
                updateStepByIndex(idx, 'done');
                return true;
            });
        }

        // Create FOTA task
        actions.push(async (idx) => {
            updateStepByIndex(idx, 'running');
            const result = await DeviceChecklistService.assignFotaConfig(imei, fileId);
            if (abortRef.current) return false;
            if (result.sessionExpired) {
                updateStepByIndex(idx, 'error', 'Session expired.');
                setGlobalError('Session expired.');
                return false;
            }
            if (!result.ok || !result.taskId) {
                updateStepByIndex(idx, 'error', result.error || 'Failed.');
                setGlobalError(result.error || t('deviceChecklist.fota_apply_failed'));
                return false;
            }
            taskIdRef.current = result.taskId;
            updateStepByIndex(idx, 'done');
            return true;
        });

        // Send web_connect
        actions.push(async (idx) => {
            updateStepByIndex(idx, 'running');
            const result = await DeviceChecklistService.sendWebConnect(deviceId);
            if (abortRef.current) return false;
            if (!result.ok) {
                updateStepByIndex(idx, 'error', result.error || 'Failed.');
                setGlobalError(result.error || t('deviceChecklist.fota_apply_failed'));
                return false;
            }
            updateStepByIndex(idx, 'done');
            return true;
        });

        // Wait for web_connect acknowledgment
        actions.push(async (idx) => {
            updateStepByIndex(idx, 'running');
            const ack = await pollSmsStatus(simCardCode, 'web_connect resp:OK');
            if (abortRef.current) return false;
            if (!ack) {
                updateStepByIndex(idx, 'error', t('deviceChecklist.fota_apply_webconnect_timeout'));
                setGlobalError(t('deviceChecklist.fota_apply_webconnect_timeout'));
                return false;
            }
            updateStepByIndex(idx, 'done');
            return true;
        });

        // Poll task status
        actions.push(async (idx) => {
            updateStepByIndex(idx, 'running');
            const result = await pollTaskStatus(taskIdRef.current!, idx);
            if (abortRef.current) return false;
            if (result === 'failed') return false;
            if (result === 'timeout') {
                updateStepByIndex(idx, 'error', t('deviceChecklist.fota_apply_timeout'));
                setGlobalError(t('deviceChecklist.fota_apply_timeout'));
                return false;
            }
            updateStepByIndex(idx, 'done');
            return true;
        });

        // Send odoset
        actions.push(async (idx) => {
            updateStepByIndex(idx, 'running');
            try {
                await DeviceChecklistService.sendOdoset({ deviceId, km });
                if (abortRef.current) return false;
                updateStepByIndex(idx, 'done');
                return true;
            } catch {
                updateStepByIndex(idx, 'error', t('deviceChecklist.odoset_sent_error'));
                setGlobalError(t('deviceChecklist.odoset_sent_error'));
                return false;
            }
        });

        // Wait for odoset acknowledgment
        actions.push(async (idx) => {
            updateStepByIndex(idx, 'running');
            const ack = await pollSmsStatus(simCardCode, `Saved Odometer Value:${Math.round(km)} km`);
            if (abortRef.current) return false;
            if (!ack) {
                updateStepByIndex(idx, 'error', t('deviceChecklist.fota_apply_odoset_timeout'));
                setGlobalError(t('deviceChecklist.fota_apply_odoset_timeout'));
                return false;
            }
            updateStepByIndex(idx, 'done');
            return true;
        });

        return actions;
    }, [imei, fileId, deviceId, simCardCode, km, updateStepByIndex, pollTaskStatus, pollSmsStatus, t]);

    const runFromStep = useCallback(async (fromIdx: number) => {
        abortRef.current = false;
        setFinished(false);
        setGlobalError(null);
        setCountdown(POLL_TIMEOUT);

        const actions = buildStepActions(isDeviceOnlineRef.current);

        try {
            for (let i = fromIdx; i < actions.length; i++) {
                if (abortRef.current) return;
                const ok = await actions[i](i);
                if (!ok) {
                    setFinished(true);
                    return;
                }
            }
            setFinished(true);
            setSuccess(true);
        } catch {
            setGlobalError(t('deviceChecklist.fota_apply_failed'));
            setFinished(true);
        }
    }, [buildStepActions, t]);

    const run = useCallback(async () => {
        abortRef.current = false;
        setFinished(false);
        setSuccess(false);
        setGlobalError(null);
        setCountdown(POLL_TIMEOUT);
        taskIdRef.current = null;
        isDeviceOnlineRef.current = false;

        // First step: check device online status
        const checkStep: StepState = { key: 'fota_apply_step_checking_status', status: 'running' };
        setSteps([checkStep]);

        let isOnline = false;
        try {
            const fota = await DeviceChecklistService.getFotaConfig(imei, deviceModelName);
            if (abortRef.current) return;
            isOnline = fota.activityStatus === 'Online';
            isDeviceOnlineRef.current = isOnline;
        } catch {
            if (abortRef.current) return;
            // If check fails, assume offline to be safe
        }

        // Build steps based on fresh online status
        const initialSteps: StepState[] = [
            { key: 'fota_apply_step_checking_status', status: 'done' },
        ];
        if (!isOnline) {
            initialSteps.push(
                { key: 'fota_apply_step_sending_setparam', status: 'pending' },
                { key: 'fota_apply_step_waiting_setparam', status: 'pending' },
            );
        }
        initialSteps.push(
            { key: 'fota_apply_step_creating_task', status: 'pending' },
            { key: 'fota_apply_step_sending_webconnect', status: 'pending' },
            { key: 'fota_apply_step_waiting_webconnect', status: 'pending' },
            { key: 'fota_apply_step_waiting', status: 'pending' },
            { key: 'fota_apply_step_sending_odoset', status: 'pending' },
            { key: 'fota_apply_step_waiting_odoset', status: 'pending' },
        );
        setSteps(initialSteps);

        await runFromStep(1);
    }, [imei, deviceModelName, runFromStep]);

    runRef.current = run;

    useEffect(() => {
        if (open) {
            runRef.current?.();
        }
        return () => {
            abortRef.current = true;
            stopCountdown();
        };
    }, [open, stopCountdown]); // eslint-disable-line

    const handleRetryStep = useCallback((stepIdx: number) => {
        // For waiting steps, retry from the corresponding sending step
        setSteps(prev => {
            const waitToSendMap: Record<string, string> = {
                fota_apply_step_waiting_setparam: 'fota_apply_step_sending_setparam',
                fota_apply_step_waiting_webconnect: 'fota_apply_step_sending_webconnect',
                fota_apply_step_waiting_odoset: 'fota_apply_step_sending_odoset',
            };
            const currentKey = prev[stepIdx]?.key;
            const sendKey = waitToSendMap[currentKey];
            const retryFrom = sendKey
                ? prev.findIndex(s => s.key === sendKey)
                : stepIdx;
            const from = retryFrom >= 0 ? retryFrom : stepIdx;

            setTimeout(() => runFromStep(from), 0);

            return prev.map((s, i) => i >= from
                ? { ...s, status: 'pending', error: undefined }
                : s
            );
        });
    }, [runFromStep]);

    const handleClose = () => {
        abortRef.current = true;
        stopCountdown();
        onClose(success);
    };

    const contentStyle: React.CSSProperties = {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        width: isMobile ? '92%' : '420px',
        maxWidth: '92%',
        padding: 0,
        backgroundClip: 'padding-box',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: '1rem',
        boxShadow: '0 16px 48px rgba(11, 17, 33, 0.16)',
        outline: '0 none',
        overflow: 'hidden',
    };
    const overlayStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        backgroundColor: 'rgba(11, 17, 33, 0.4)',
        zIndex: 1001,
        backdropFilter: 'blur(2px)',
    };

    const renderStepIcon = (status: StepStatus) => {
        switch (status) {
            case 'done':
                return <FontAwesomeIcon icon={faCheck} className="text-emerald-500 text-xs" />;
            case 'error':
                return <FontAwesomeIcon icon={faTimes} className="text-red-500 text-xs" />;
            case 'running':
                return <FontAwesomeIcon icon={faCircleNotch} className="text-sky-500 text-xs animate-spin" />;
            case 'info':
                return <FontAwesomeIcon icon={faClock} className="text-amber-500 text-xs" />;
            default:
                return <span className="w-3 h-3 rounded-full border-2 border-slate-200 inline-block" />;
        }
    };

    const completedCount = steps.filter(s => s.status === 'done' || s.status === 'info').length;
    const progressPct = steps.length > 0 ? (completedCount / steps.length) * 100 : 0;

    const isWaitingStep = (key: string) =>
        key === 'fota_apply_step_waiting' ||
        key === 'fota_apply_step_waiting_setparam' ||
        key === 'fota_apply_step_waiting_webconnect' ||
        key === 'fota_apply_step_waiting_odoset';

    return (
        <Modal isOpen={open} style={{ content: contentStyle, overlay: overlayStyle }}>
            <div>
                {/* Header */}
                <div className="bg-slate-900 px-4 py-4 flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center shrink-0 text-white">
                        <FontAwesomeIcon icon={faGear} className={!finished ? 'animate-spin' : ''} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-white font-bold text-sm leading-tight m-0 truncate">
                            {finished && success
                                ? t('deviceChecklist.fota_apply_step_done')
                                : t('deviceChecklist.fota_edit_config')}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5 m-0">Teltonika FOTA Web</p>
                    </div>
                </div>

                {/* Steps */}
                <div className="px-4 py-4 space-y-3">
                    {steps.map((step, idx) => (
                        <div key={step.key} className="flex items-start gap-3">
                            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                                {renderStepIcon(step.status)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <p className={`text-sm m-0 leading-snug ${
                                        step.status === 'done'
                                            ? 'text-slate-400'
                                            : step.status === 'error'
                                                ? 'text-red-600 font-semibold'
                                                : step.status === 'running'
                                                    ? 'text-slate-800 font-semibold'
                                                    : step.status === 'info'
                                                        ? 'text-amber-600'
                                                        : 'text-slate-300'
                                    }`}>
                                        {t(`deviceChecklist.${step.key}`)}
                                        {isWaitingStep(step.key) && step.status === 'running' && (
                                            <span className="ml-2 text-sky-500 font-mono text-xs">
                                                {countdown}s
                                            </span>
                                        )}
                                    </p>
                                    {step.status === 'error' && finished && (
                                        <button
                                            type="button"
                                            onClick={() => handleRetryStep(idx)}
                                            title={t('deviceChecklist.retry')}
                                            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg border border-red-200 text-red-400 hover:border-red-400 hover:text-red-500 hover:bg-red-50 active:scale-95 transition-all"
                                        >
                                            <FontAwesomeIcon icon={faRotateRight} className="text-[11px]" />
                                        </button>
                                    )}
                                </div>
                                {step.error && (step.status === 'error' || step.status === 'info') && (
                                    <p className="text-xs text-red-500 mt-0.5 m-0">{step.error}</p>
                                )}
                                {step.key === 'fota_apply_step_waiting_setparam' && step.status === 'error' && (
                                    <p className="text-xs text-amber-600 mt-0.5 m-0">{t('deviceChecklist.fota_apply_setparam_timeout_hint')}</p>
                                )}
                                {step.error && step.status === 'done' && (
                                    <p className="text-xs text-sky-500 mt-0.5 m-0">{step.error}</p>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Progress bar */}
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2">
                        <div
                            className="h-full rounded-full transition-all duration-500 ease-out"
                            style={{
                                width: `${progressPct}%`,
                                background: globalError && !success
                                    ? '#ef4444'
                                    : 'linear-gradient(to right, #38bdf8, #34d399)',
                            }}
                        />
                    </div>

                    {/* Close button — only shown when finished */}
                    {finished && (
                        <div className="flex justify-end pt-2">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 active:scale-95 text-white text-sm font-semibold rounded-xl px-5 py-2.5 border-0 transition-all"
                            >
                                {t('deviceChecklist.fota_apply_close')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default FotaApplyModal;
